import styled from '@emotion/styled'
import { observer } from 'mobx-react'

import { DisclosureToggle, HelpIcon, TooltipText } from '../../../components'
import { includesRetiredSpouse, numberToDollars } from '../../../utils'

const Section = styled.div`
  display: flex;
  flex-direction: column;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const HeaderHelp = styled(HelpIcon)`
  /* Remove extra line-height space around the icon. */
  line-height: 0;

  /* Nudge the icon down to align visually with the text. */
  margin-top: 2px;

  /* Increase specificity over normalize.css, whose SVG overflow rule clips the stroke. */
  && svg {
    overflow: visible;
  }
`

const ToggleButton = styled(DisclosureToggle)`
  gap: 16px;
  color: ${p => p.theme.mediumGray};
  font-size: 20px;
  font-weight: 300;
  line-height: 24px;
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 16px;
  color: ${p => p.theme.mediumGray};
  font-size: 14px;
  font-weight: 300;
  line-height: 24px;
`

const Group = styled.div`
  display: flex;
  flex-direction: column;
`

const Paragraph = styled.p`
  margin: 0;
`

const Footnote = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 24px;
`

const Emphasis = styled.span`
  font-weight: 600;
`

/*
 * Sits beside "Today's monthly value" when a retired spouse is included, where
 * the amount looks smaller than the household is used to living on. Exported so
 * the screens that show it stay word-for-word with the section it points at.
 */
export const SEEM_LOW_NOTE = 'Seem low? Expand “Tell me more” area below'

const dollars = value => `$${numberToDollars(value, true)}`

const IncomeReplacementTellMeMore = ({ percent, expanded, onToggle, source }) => {
  const {
    account: { annualSalary },
    person: { maritalStatus, includeSpouse, otherIncome },
    spouse,
    baseCase,
  } = source

  // `baseCase` is null until the engine responds and every figure below comes off it, so
  // render nothing rather than a walk-through of zeros. The toggle appears once it loads.
  if (!baseCase) return null

  const { incomeBeforeTax, currentTaxRate, taxAmount, clientContributions, spouseContributions } =
    baseCase

  // Null until the spouse record loads; unread unless one is included anyway.
  const { annualIncome, firstName, otherIncome: spouseOtherIncome } = spouse ?? {}
  const spouseRetired = includesRetiredSpouse({ person: { maritalStatus, includeSpouse }, spouse })
  /*
   * A retired spouse earns nothing for the engine to replace, so their income
   * and contributions are absent from the base case and the walk-through below
   * is the single-person one. The "Important" note at the top is the only place
   * they are mentioned — it explains the figure rather than adding to it.
   */
  const spouseEarning = Boolean(maritalStatus && includeSpouse) && !spouseRetired
  const taxRatePercent = Math.round(currentTaxRate * 10000) / 100
  const afterTaxIncome = incomeBeforeTax - taxAmount
  const contributions = clientContributions + (spouseEarning ? spouseContributions : 0)
  const afterContributions = afterTaxIncome - contributions
  const monthlyTakeHome = afterContributions / 12
  const combined = spouseEarning ? ' combined' : ''

  return (
    <Section>
      <Header>
        <ToggleButton expanded={expanded} onToggle={onToggle}>
          Tell me more
        </ToggleButton>
        <HeaderHelp size='large' tooltip={TooltipText.tellMeMoreHelp(percent)} />
      </Header>

      {expanded && (
        <Body>
          {spouseRetired && (
            <Paragraph>
              <Emphasis>Important</Emphasis>: While {firstName} earned{' '}
              {dollars(annualIncome + spouseOtherIncome)} a year* prior to retiring, GuidedChoice
              bases its projected income replacement on income currently earning only. Since you are
              likely used to living off a combined total of take-home pay, we suggest changing your
              income goal accordingly.
            </Paragraph>
          )}

          <Group>
            <Paragraph>You earn {dollars(annualSalary + otherIncome)} a year.*</Paragraph>

            {spouseEarning && (
              <Paragraph>
                {firstName} earns {dollars(annualIncome + spouseOtherIncome)} a year.* Together,
                this is a total of {dollars(incomeBeforeTax)}.
              </Paragraph>
            )}

            <Paragraph>
              We estimate a tax rate of {taxRatePercent}%, or {dollars(taxAmount)}.
            </Paragraph>

            <Paragraph>
              This leaves you with {dollars(afterTaxIncome)} in{combined} take-home pay after taxes
              without considering your retirement plan contributions.
            </Paragraph>
          </Group>

          {/* Explain contributions whenever they affect the take-home calculation. */}
          {contributions > 0 && (
            <Group>
              <Paragraph>
                Based on your current contribution rate, you are on target to have contributed a
                total of {dollars(clientContributions)} by the end of the year.
                {spouseEarning &&
                  ` ${firstName} is on target to have contributed a total of ${dollars(
                    spouseContributions
                  )}. This is a combined total of ${dollars(contributions)}.`}
              </Paragraph>

              <Paragraph>
                Subtract this from your previous{combined} take-home pay, {dollars(afterTaxIncome)},
                and you are left with {dollars(afterContributions)}.
              </Paragraph>
            </Group>
          )}

          <Group>
            <Paragraph>
              Divide by 12 months to get your{combined} monthly take-home pay:{' '}
              {dollars(monthlyTakeHome)}.
            </Paragraph>

            <Paragraph>
              {percent}% of this is {dollars((monthlyTakeHome * percent) / 100)} each month in
              spendable income.
            </Paragraph>
          </Group>

          <Footnote>*Compensation plus other earned income, if any.</Footnote>
        </Body>
      )}
    </Section>
  )
}

export default observer(IncomeReplacementTellMeMore)
